import { useGetBlockedRecordByBlockedUserIdQuery } from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { omit } from '@shared/utils';
import { createContext, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type { BlockedRecordDetailsContextValue, BlockedRecordDetailsProviderProps } from './types';

const BlockedRecordDetailsContext = createContext<BlockedRecordDetailsContextValue | undefined>(
  undefined,
);

export default function BlockedRecordDetailsProvider(
  props: Readonly<BlockedRecordDetailsProviderProps>,
) {
  const { blockedUserId, blockedRecordId, children } = props;
  const { showSnackbar } = useSnackbar();

  const { data: getBlockedRecordResult, loading: gettingBlockedRecord } =
    useGetBlockedRecordByBlockedUserIdQuery({
      variables: {
        input: {
          id: blockedUserId,
        },
      },
    });

  const { blockedUser, blockedRecords } = useMemo(() => {
    const data = getBlockedRecordResult?.ecGetBlockedUserById;
    if (!data?.blockedUser) {
      return { blockedUser: null, blockedRecords: null };
    }

    const currentBlockedRecord = data.blockedUser.blockedRecords.find(
      (record) => record.id === blockedRecordId,
    );
    if (!currentBlockedRecord) {
      return { blockedUser: data.blockedUser, blockedRecords: null };
    }

    return {
      blockedUser: omit(data.blockedUser, 'blockedRecords'),
      blockedRecords: currentBlockedRecord,
    };
  }, [getBlockedRecordResult, blockedRecordId]);

  const contextValue: BlockedRecordDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      blockedUser: blockedUser!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      blockedRecord: blockedRecords!,
    };
  }, [blockedUser, blockedRecords]);

  useEffect(() => {
    if ((blockedUser === null || blockedRecords === null) && !gettingBlockedRecord) {
      showSnackbar({
        message: 'ไม่พบข้อมูลรายชื่อผู้ถูกระงับสิทธิ์ดังกล่าว',
        variant: 'muted',
      });
    }
  }, [blockedUser, blockedRecords, gettingBlockedRecord, showSnackbar]);

  if (gettingBlockedRecord) {
    return <Loading height='100%' />;
  }

  if (blockedUser === null || blockedRecords === null) {
    return <Navigate to='/admin/blocklist' />;
  }

  return (
    <BlockedRecordDetailsContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </BlockedRecordDetailsContext.Provider>
  );
}

export { BlockedRecordDetailsContext };
